import { ISchemaObject, IItemReference, IDynamicContext, ItemWithPath } from '@augloop/types-core';
import { Range } from './annotations';

// Range of text with formatting properties.
export interface IFormattedRange extends Range {
    // Is the range deleted by a tracked change
    readonly isDeleted?: boolean;

    // Font family name
    readonly fontFamily?: string;

    // Is entire span bold.
    readonly fontWeight?: FontWeight;

    // Font size in points used in span.
    /**
        @cpptype std::optional<double>
        @cppedgetype absl::optional<double>
        @csharptype double?
        @scriptsharptype double?
        @javatype Optional<double>
    */
    readonly fontSize?: number;

    // Is entire span italic.
    readonly italic?: boolean;

    // Does entire span have underline.
    readonly underline?: UnderlineType;

    // Does entire span has all caps applied.
    readonly capitalization?: CapsType;

    /**
     * Font color in paragraph. Every byte has one of RGBA properties.
     * @csharptype uint?
     * @scriptsharptype uint?
     */
    readonly color?: number;

    /**
     * Font highlight color in paragraph. Every byte has one of RGBA properties.
     * @csharptype uint?
     * @scriptsharptype uint?
     */
    readonly highlightColor?: number;

    // Name of the style applied to span.
    readonly styleName?: string;

    // Is the range inside a follow up
    readonly isFollowUp ?: boolean;

    // Hyperlink target, if any
    readonly link?: string;

    // Does the range refer to fields like comments, footnotes, endnotes, bookmarks, building blocks
    readonly isReference?: boolean;

    // LanguageId for range (format: RFC 4646, see CultureInfo for more details)
    readonly languageId?: string;

    // Object type which the formatted range represents (e.g. Image, Chart, SmartArt, etc.)
    // It is not set if the formatted range is plain text.
    readonly objectType?: ObjectType;
}

export enum AnchorMode {
    Unspecified = 0,
    Inline,
    Floating
}

export interface IObjectReference extends IItemReference {
    // Index of the formatted range which is the placeholder for the referenced object. Provided only for Inline AnchorMode.
    readonly formattedRangeIndex?: number;

    readonly anchorMode?: AnchorMode;

    // Anchored objects are linked directly to the text tile, not the formatted range.
    // Since there isn't a corresponding formatted range where we store ObjectType,
    // we're adding anchoredObjectType property to store that information.
    // NOTE: We consider anchoredObjectType to be a subset of ObjectType (image, chart, and similar Word construcs).
    // It cannot be a special caracter like tab or page break.
    readonly anchoredObjectType?: ObjectType;
}

// A tile with text content
export interface ITextTile extends ISchemaObject {
    // The actual raw content of the tile (paragraph text)
    // This should be raw text with no special formatting or RTF text
    readonly content: string;
}

// Tile containing formatting properties currently used by Heading detection workflow
export interface IFormattedTextTile extends ITextTile {
    // Indentation of paragraph in points (pt). If there is no indentation the value is 0
    readonly indentation?: number;

    // Outline level of the paragraph as represented in navigation.
    readonly outlineLevel?: number;

    // Type of the list that the paragraph is part of.
    readonly listType?: ListType;

    // Alignment of paragraph.
    readonly alignment?: Alignment;

    // Text ranges/runs containing formatting information. Ranges are expected to be preserved in reading order.
    // There should be no overlapping between ranges.
    // It is allowed to cover only part of the text with formatted ranges. Rest of the text is supposed to have default formatting.
    // E.g. in a 100-character text tile it would be ok to have a single FormattedRange with start=35 and length=10;
    // this would be equivalent to also having FormattedRanges covering s=0, l=35 and s=45, l=55 with none of the (all-optional)
    // properties set.
    readonly formattedRanges?: IFormattedRange[];

    // Returns if text tile inside table or not.
    readonly isInsideTable?: boolean;

    /** Returns if text tile is inside TOC (Table of Contents) or not. */
    readonly isInsideTOC?: boolean;

    // List of references to other items anchored within text. For example inline images would have one entry here refering to image tile.
    readonly objectReferences?: IObjectReference[];

    // Spacing before paragraph in points (pt)
    /**
        @cpptype std::optional<double>
        @cppedgetype absl::optional<double>
        @csharptype double?
        @scriptsharptype double?
        @javatype Optional<double>
    */
    readonly spacingBeforeParagraph?: number;

    // Spacing after paragraph in points (pt)
    /**
        @cpptype std::optional<double>
        @cppedgetype absl::optional<double>
        @csharptype double?
        @scriptsharptype double?
        @javatype Optional<double>
    */
    readonly spacingAfterParagraph?: number;

    // Line spacing inside paragraph in lines
    /**
        @cpptype std::optional<double>
        @cppedgetype absl::optional<double>
        @csharptype double?
        @scriptsharptype double?
        @javatype Optional<double>
    */
    readonly lineSpacing?: number;

    // Right Indentation of paragraph in points (pt). If there is no indentation the value is 0
    readonly rightIndentation?: number;

    // if paragraph is list item, this will provide it's parent list id
    readonly listId? : string;

    // Font size set on bullet/number of list item.
    readonly bulletAndNumberFontSize?: number;

    // Font family set on bullet/number of list item.
    readonly bulletAndNumberFontFamily?: string;

    // Type of the special indent of the paragraph.
    readonly specialIndentType?: SpecialIndentType;

    // Value of the special indent of the paragraph.
    readonly specialIndentBy?: number;

    // The font-kerning property controls the usage of the kerning information stored in a font.
    // Tip: Kerning defines how letters are spaced.
    readonly fontKerning?: number;
}

/**
 * Represents all the context information for dynamic workflow execution on text.
 * Similar interfaces can be constructed for grid context, ink context, etc.
 */
export interface IDynamicTextContext extends IDynamicContext {
    /**
     * Contains textTile items serving as context above input requested by dynamicExecutionPreferences.
     */
    contextAbove?: ItemWithPath[];

    /**
     * Contains textTile items serving as context below input requested by dynamicExecutionPreferences.
     */
    contextBelow?: ItemWithPath[];
}

// Tile containing a task that may have additional formatting
// and references to other objects in the document
export interface ITaskTile extends IFormattedTextTile {
}

// Tile Containing a "Person"
// content - display string (typically the user's name)
export interface IPersonTile extends ITextTile {
    readonly upn: string;
}

// Tile containing a "date"
// content - display string (the date in a human readable format)
export interface IDateTile extends ITextTile {
    // Milliseconds since 1/1/1970 UTC
    readonly timestamp: number;
}

// Enum containing possible list type values.
export enum ListType {
    Undefined = 0,
    Bullet,
    Numbered,
    Lim,
    Nil,
    Any,
    NoList,
    Invalid,
}

// Enum containing possible font weight values.
export enum FontWeight {
    DontCare = 1,
    Thin,
    ExtraLight,
    Light,
    Normal,
    Medium,
    SemiBold,
    Bold,
    ExtraBold,
    Heavy
}

// Enum containing possible capitalization values.
export enum CapsType {
    NoCaps = 0,
    SmallCaps,
    AllCaps,
    AllPetiteCaps,
    PetiteCaps,
    Unicase,
    Titling,
    Other
}

// Enum containing possible underline types.
export enum UnderlineType {
    None = 0,
    Words,
    SingleLine,
    DoubleLine,
    HeavyLine,
    DottedLine,
    DottedHeavyLine,
    DashLine,
    DashHeavyLine,
    DashLongLine,
    DashLongHeavyLine,
    DotDashLine,
    DotDashHeavyLine,
    DotDotDashLine,
    DotDotDashHeavyLine,
    WavyLine,
    WavyHeavyLine,
    WavyDoubleLine
}

// Enum containing possible paragraph alignment
export enum Alignment {
    Left = 0,
    Right,
    Center,
    Justified,
    Other,
    None,
    JustifyLow,
    Distributed,
    ThaiDistributed,
}

// Enum containing possible object types.
export enum ObjectType
{
    Image = 0,
    TextBox,
    Chart,
    SmartArt,
    Drawing,
    EquationObject,
    Ink,
    InkSpace,
    InkEndOfLine,
    LineBreak,
    PageBreak,
    PageNumbers,
    FootnoteEndnote,
    FootnoteEndnoteReferenceMark,
    Tab,
    UnsupportedObject,
    Other
}

// Enum containing possible SpecialIndent type values.
export enum SpecialIndentType {
    Initial = 0,
    FirstLine = 1,
    Hanging = 2,
}
